import { styled } from "@mui/material/styles";
import { HTMLAttributes } from "react";

import type { Hbml, HbmlElementNode } from "@holibob-packages/hbml";

import { HbmlRenderer, HbmlRendererComponents } from "../../components";
import {
    HbmlBlockquoteNode,
    HbmlBulletedListNode,
    HbmlDividerNode,
    HbmlElements,
    HbmlLinkNode,
    HbmlListItemContentNode,
    HbmlListItemNode,
    HbmlNumberedListNode,
    HbmlParagraphNode,
    HbmlRawTextNode,
    HbmlTableCellNode,
    HbmlTableNode,
    HbmlTableRowNode,
} from "../../components/HbmlComponents";
import { Typography } from "../../components/Typography";

export type ProductContentHbmlRendererProps = { content: Hbml; componentOverrides?: HbmlRendererComponents };

function ParagraphComponent(props: HTMLAttributes<HTMLParagraphElement>) {
    return <Typography {...props} size="large" color="subtle" />;
}

const basicComponents = {
    [HbmlElements.RAW_TEXT]: HbmlRawTextNode,
    [HbmlElements.PARAGRAPH]: function ParagraphNode({
        content,
    }: {
        content: HbmlElementNode & { type: "paragraph" };
    }) {
        return <HbmlParagraphNode.nodeComponent content={content} component={ParagraphComponent} />;
    },
    [HbmlElements.BLOCKQUOTE]: HbmlBlockquoteNode,
    [HbmlElements.DIVIDER]: HbmlDividerNode.nodeComponent,
    [HbmlElements.LIST_ITEM]: HbmlListItemNode,
    [HbmlElements.LIST_ITEM_CONTENT]: HbmlListItemContentNode,
    [HbmlElements.LINK]: HbmlLinkNode.nodeComponent,
    [HbmlElements.TABLE]: HbmlTableNode.nodeComponent,
    [HbmlElements.TABLE_ROW]: HbmlTableRowNode.nodeComponent,
    [HbmlElements.TABLE_CELL]: HbmlTableCellNode.nodeComponent,
    [HbmlElements.NUMBERED_LIST]: HbmlNumberedListNode,
    [HbmlElements.BULLETED_LIST]: HbmlBulletedListNode,
};

export function ProductContentHbmlRenderer({ content, componentOverrides }: ProductContentHbmlRendererProps) {
    const finalComponents = { ...basicComponents, ...componentOverrides };
    return (
        <Wrapper data-testid="product-content-hbml">
            <HbmlRenderer content={content} components={finalComponents} />
        </Wrapper>
    );
}

const Wrapper = styled("div")({
    "> p": {
        "&:first-child": {
            marginTop: "unset",
            paddingInlineStart: "unset",
        },
        "&:last-child": {
            marginBottom: "unset",
            paddingInlineEnd: "unset",
        },
    },
    "& p": {
        marginBlockStart: "1rem",
        marginBlockEnd: "1rem",
    },
});
